import Vue from 'vue';
import { sync } from 'vuex-router-sync';
import type { RawLocation, Location } from 'vue-router';
import initPlugins from '@/common/plugins';
import getRouter from '@/self-service/routes';
import { store } from '@/common/root';
import Application from '@/common/application';
import { createPinia, mapState, mapStores, PiniaVuePlugin } from 'pinia';
import useAuthStore from '@/common/store/pinia/auth';
import { initApi } from '@/common/utils/api';
import initSubscriptions from '@/common/utils/subscriptions';
import useTelemetryStore from '@/common/store/pinia/telemetry';
import useAppModeStore from '@/common/store/pinia/appMode';
import getAppMode from '@/common/utils/getAppMode';
import wsPlugin from '@/common/store/plugins/wsPlugin';
import usePermissionsStore from '@/common/store/pinia/permissions';
import App from './SelfServiceApp.vue';

type ExtendedRawLocation = RawLocation | Location & {
  query?: {
    redirectUrl?: string;
    logout?: boolean;
  }
}

Vue.use(PiniaVuePlugin);
const pinia = createPinia();
pinia.use(wsPlugin);

useTelemetryStore(pinia).initialize();

export default async ({ providers = {}, settings = {} }: AppOptions = {}) => {
  const appModeStore = useAppModeStore(pinia);
  const appMode = getAppMode();

  appModeStore.configure(appMode);
  appModeStore.setSettings(settings);

  initApi({
    httpAdapterFromProvider: providers.httpAdapter,
    authStore: useAuthStore(pinia),
    appModeStore
  });
  initPlugins(pinia);
  initSubscriptions();

  await appModeStore.preset.initSelfService({
    authStore: useAuthStore(pinia),
    permissionsStore: usePermissionsStore(pinia),
    providers
  }).catch(console.error);

  const router = getRouter(pinia);
  sync(store, router, { moduleName: 'routes' });

  const app = new Vue({
    extends: Application,
    router,
    pinia,
    computed: {
      ...mapStores(useAuthStore),
      ...mapState(useAuthStore, ['currentUser'])
    },
    watch: {
      currentUser(value) {
        if (!value) {
          const redirectUrl = this.$route.query.redirectUrl
            || this.$route.fullPath;
          const currentDomain = typeof window !== 'undefined' && window.localStorage.getItem('domain');
          const idpId = this.authStore.currentIdpId;
          const routeParam: ExtendedRawLocation = {
            path: `/login/${currentDomain || ''}`
          };
          if (routeParam.path !== redirectUrl) {
            routeParam.query = { redirectUrl };
          }

          if (idpId !== null && routeParam.query) {
            routeParam.path = `/login/${currentDomain || ''}/${idpId}`;
            routeParam.query.logout = true;
          }
          this.authStore.resetIdpId();
          this.$router.push(routeParam as RawLocation);
        }
      }
    },
    render: h => h(App),
    provide: providers,
  });

  return { app, pinia };
};
